/**
 * accessMeta — the ONE place that maps access vocabulary to visuals + i18n.
 *
 * Source kinds, resource-type icons, role/relation labels and expiry buckets all
 * resolve here, so no component hardcodes a label or a per-kind colour. The
 * source hues are CATEGORY tints (glyph + tint + label), never an action accent
 * — every chip carries a distinct glyph AND its i18n label, so identity survives
 * greyscale and colour-blindness (DESIGN.md addendum; status never colour-only).
 */

import {
  Building2,
  CornerDownRight,
  FileText,
  Globe,
  HeartPulse,
  KeyRound,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react'
import type { AccessSource, ResourceType } from './types'

export interface SourceMeta {
  icon: LucideIcon
  /** Chip background + text (a soft tint wash + a ≥4.5:1 ink). */
  chip: string
  /** Timeline node marker (icon-in-circle) fill + ring. */
  node: string
  /** i18n key for the source's short label. */
  labelKey: string
}

/**
 * direct is the neutral baseline; the derived paths each take a distinct
 * category hue from the token ramps already used as agent/verdict identity
 * (violet, terracotta, citation-amber, tone-teal). admin is the lone inverted
 * chip — it is rare (a platform override) and should read as elevated.
 */
export const SOURCE_META: Record<AccessSource, SourceMeta> = {
  direct: {
    icon: UserRound,
    chip: 'bg-neutral-100 text-neutral-700',
    node: 'bg-neutral-100 text-neutral-600 ring-neutral-200',
    labelKey: 'access.source.direct',
  },
  group_membership: {
    icon: Users,
    chip: 'bg-violet-50 text-violet-700',
    node: 'bg-violet-50 text-violet-700 ring-violet-200',
    labelKey: 'access.source.group_membership',
  },
  org_cascade: {
    icon: Building2,
    chip: 'bg-tone-ok-tint text-tone-ok',
    node: 'bg-tone-ok-tint text-tone-ok ring-tone-ok/25',
    labelKey: 'access.source.org_cascade',
  },
  patient_care_team: {
    icon: HeartPulse,
    chip: 'bg-terracotta-50 text-terracotta-700',
    node: 'bg-terracotta-50 text-terracotta-700 ring-terracotta-200',
    labelKey: 'access.source.patient_care_team',
  },
  team_supervision: {
    icon: ShieldCheck,
    chip: 'bg-citation-50 text-citation-700',
    node: 'bg-citation-50 text-citation-700 ring-citation-200',
    labelKey: 'access.source.team_supervision',
  },
  public: {
    icon: Globe,
    chip: 'bg-neutral-100 text-neutral-600',
    node: 'bg-neutral-100 text-neutral-500 ring-neutral-200',
    labelKey: 'access.source.public',
  },
  admin: {
    icon: KeyRound,
    chip: 'bg-neutral-900 text-neutral-0',
    node: 'bg-neutral-900 text-neutral-0 ring-neutral-700',
    labelKey: 'access.source.admin',
  },
  // Structural cascade the resource-list scan couldn't attribute to one path
  // (org cascade / care team / supervision): neutral chip, "step-into" glyph.
  // A tap on "Why?" fetches the explanation endpoint, which resolves the exact
  // chain and re-labels each hop with its precise source.
  inherited: {
    icon: CornerDownRight,
    chip: 'bg-neutral-100 text-neutral-600',
    node: 'bg-neutral-100 text-neutral-600 ring-neutral-200',
    labelKey: 'access.source.inherited',
  },
}

/** Icons for the known resource domains; unknown types fall back to a generic glyph. */
const RESOURCE_ICONS: Record<string, LucideIcon> = {
  session: MessageSquare,
  patient: HeartPulse,
  document: FileText,
  feature: Sparkles,
  unit: Building2,
}

export function resourceIcon(type: ResourceType): LucideIcon {
  return RESOURCE_ICONS[type] ?? KeyRound
}

/** i18n key for a resource-type label (fallback to the raw type at the call site). */
export function resourceTypeKey(type: ResourceType): string {
  return `access.resourceType.${type}`
}

/** i18n key for a role/relation label (fallback to the raw value at the call site). */
export function roleKey(role: string): string {
  return `access.role.${role}`
}

/** i18n key for a unit's `kind` (org.location_kinds is config-driven, so a
 *  deployment's kind vocabulary isn't fixed here — fall back to the raw
 *  value at the call site, same convention as resourceTypeKey/roleKey). */
export function locationKindKey(kind: string): string {
  return `access.locationKind.${kind}`
}

export function relationKey(relation: string): string {
  return `access.relation.${relation}`
}

export type RemainingUnit = 'min' | 'hour' | 'day'

export interface Remaining {
  unit: RemainingUnit
  value: number
  /** ≤2h out — surfaced in amber, the "act soon" signal. */
  soon: boolean
  /** Already elapsed — the grant has lapsed. */
  expired: boolean
}

/**
 * Coarse "time remaining" bucket for an ISO expiry. Coarse on purpose — a nurse
 * needs "in ~2h" / "morgen", not a live countdown. `now` is injectable so the
 * dev fixtures and any test render deterministically.
 */
export function remaining(expiresAt: string, now: number = Date.now()): Remaining {
  const ms = new Date(expiresAt).getTime() - now
  if (ms <= 0) return { unit: 'min', value: 0, soon: true, expired: true }
  const mins = Math.round(ms / 60_000)
  if (mins < 60) return { unit: 'min', value: Math.max(1, mins), soon: true, expired: false }
  const hours = Math.round(mins / 60)
  if (hours < 24) return { unit: 'hour', value: hours, soon: hours <= 2, expired: false }
  const days = Math.round(hours / 24)
  return { unit: 'day', value: days, soon: false, expired: false }
}
